import { CHARACTER_WHITESPACE } from '../../constants/common'
import { isDevelopmentEnvironment } from '../../utils/common'
import { formatIntlList } from '../../utils/locale'
import { ICON_CHEVRON_DIRECTION } from '../Icon'

import {
  GRETA_AI_CHAT_PROJECT_META_SEPARATOR,
  GretaAIChatPanelView,
  GretaAIChatProjectSubjectType,
} from './GretaAIChat.constants'
import { getIsGretaAIChatRowGenerating } from './GretaAIChat.utils'

function getGretaAIChatProjectBuildingName(building, t) {
  return building.hasAccess
    ? building.address
    : t('Greta.ProjectScopeBuildingNumberKey', { number: building.number })
}

function getIsGretaAIChatProjectAboutBuilding(subject) {
  return subject?.type === GretaAIChatProjectSubjectType.Building
}

function getIsGretaAIChatProjectAboutRequests(subject) {
  return subject?.type === GretaAIChatProjectSubjectType.Requests
}

function getHasGretaAIChatProjectDeleteContent({ count }) {
  return count > 0
}

function getGretaAIChatProjectDeleteContents(
  { chatsCount, documentsCount, textsCount },
  t
) {
  return [
    {
      count: chatsCount,
      text: t('Greta.ProjectDeleteChatsKey', { count: chatsCount }),
    },
    {
      count: documentsCount,
      text: t('Greta.ProjectDeleteDocumentsKey', { count: documentsCount }),
    },
    {
      count: textsCount,
      text: t('Greta.ProjectDeleteTextsKey', { count: textsCount }),
    },
  ]
    .filter(getHasGretaAIChatProjectDeleteContent)
    .map(({ text }) => text)
}

export function getIsGretaAIChatProjectsEnabled() {
  return isDevelopmentEnvironment()
}

export function getIsGretaAIChatProjectsView(view) {
  return view === GretaAIChatPanelView.Projects
}

export function getGretaAIChatProjectScopeLabel(subject, t) {
  if (getIsGretaAIChatProjectAboutBuilding(subject)) {
    return subject.building.hasAccess
      ? t('Greta.ProjectScopeBuildingKey', {
          address: subject.building.address,
        })
      : getGretaAIChatProjectBuildingName(subject.building, t)
  }

  if (getIsGretaAIChatProjectAboutRequests(subject)) {
    const [firstRequest] = subject.requests
    const requestsLabel = t('Greta.ProjectScopeRequestsKey', {
      count: subject.requests.length,
      number: firstRequest.number,
    })

    return [
      requestsLabel,
      getGretaAIChatProjectBuildingName(subject.building, t),
    ].join(GRETA_AI_CHAT_PROJECT_META_SEPARATOR)
  }

  return t('Greta.ProjectScopeEmptyKey')
}

export function getGretaAIChatExpandChevronDirection(isExpanded) {
  return isExpanded ? ICON_CHEVRON_DIRECTION.DOWN : ICON_CHEVRON_DIRECTION.RIGHT
}

export function getHasGretaAIChatProjects(projects) {
  return projects.length > 0
}

export function getHasGretaAIChatProjectChats(projectChats) {
  return projectChats.length > 0
}

export function getHasGretaAIChatProjectChat(projectChats, chatId) {
  return projectChats.some(chat => chat.id === chatId)
}

export function getGretaAIChatProjectChatIds(projects) {
  return new Set(projects.flatMap(project => project.chatIds))
}

export function getIsGretaAIChatProjectGenerating(
  projectChats,
  chatStatusById
) {
  return projectChats.some(chat =>
    getIsGretaAIChatRowGenerating(chatStatusById[chat.id])
  )
}

export function getGretaAIChatProjectDeleteDescription({
  isGenerating,
  language,
  project: { chatIds, documentsCount, name, textsCount },
  t,
}) {
  const contents = getGretaAIChatProjectDeleteContents(
    { chatsCount: chatIds.length, documentsCount, textsCount },
    t
  )

  const description =
    contents.length > 0
      ? t('Greta.ProjectDeleteDescriptionKey', {
          contents: formatIntlList(contents, language),
          name,
        })
      : t('Greta.ProjectDeleteEmptyDescriptionKey', { name })

  return isGenerating
    ? [t('Greta.ProjectDeleteGeneratingKey'), description].join(
        CHARACTER_WHITESPACE
      )
    : description
}
